import React from 'react';

import type { EditorState } from 'prosemirror-state';

import { textColor } from '@atlaskit/adf-schema';
import type { ToolbarUIComponentFactory } from '@atlaskit/editor-common/types';
import { fg } from '@atlaskit/platform-feature-flags';

import type { CompanyHubTextColorPlugin } from './companyHubTextColorPluginType';
import { changeColor } from './editor-commands/change-color';
import {
	type CompanyHubTextColorPluginConfig,
	pluginKey as companyHubTextColorPluginKey,
	createPlugin,
} from './pm-plugins';
import { PrimaryToolbarComponent } from './ui/company-hub-text-color-plugin/PrimaryToolbarComponent';
import { getToolbarComponents } from './ui/toolbar-components';

const pluginConfig = (
	textColorConfig?: CompanyHubTextColorPluginConfig | boolean,
): CompanyHubTextColorPluginConfig | undefined => {
	if (!textColorConfig || typeof textColorConfig === 'boolean') {
		return undefined;
	}

	return textColorConfig;
};

export const companyHubTextColorPlugin: CompanyHubTextColorPlugin = ({
	config: textColorConfig,
	api,
}) => {
	const isToolbarAIFCEnabled = Boolean(api?.toolbar);

	const primaryToolbarComponent: ToolbarUIComponentFactory = ({
		editorView,
		popupsMountPoint,
		popupsBoundariesElement,
		popupsScrollableElement,
		isToolbarReducedSpacing,
		dispatchAnalyticsEvent,
		disabled,
	}) => {
		if (!editorView) {
			return null;
		}

		return (
			<PrimaryToolbarComponent
				isReducedSpacing={isToolbarReducedSpacing}
				editorView={editorView}
				popupsMountPoint={popupsMountPoint}
				popupsBoundariesElement={popupsBoundariesElement}
				popupsScrollableElement={popupsScrollableElement}
				dispatchAnalyticsEvent={dispatchAnalyticsEvent}
				disabled={disabled}
				api={api}
			/>
		);
	};

	if (
		isToolbarAIFCEnabled &&
		api?.toolbar?.actions.registerComponents &&
		fg('platform_editor_toolbar_aifc_companyhub_text_color')
	) {
		api.toolbar.actions.registerComponents(getToolbarComponents(api));
	} else {
		api?.primaryToolbar?.actions.registerComponent({
			name: 'textColor',
			component: primaryToolbarComponent,
		});
	}

	return {
		name: 'companyHubTextColor',

		marks() {
			return [{ name: 'companyHubTextColor', mark: textColor }];
		},

		pmPlugins() {
			return [
				{
					name: 'companyHubTextColor',
					plugin: ({ dispatch }) => createPlugin(dispatch, pluginConfig(textColorConfig)),
				},
			];
		},

		getSharedState(editorState?: EditorState) {
			if (!editorState) {
				return undefined;
			}
			return companyHubTextColorPluginKey.getState(editorState);
		},

		actions: {
			changeColor: (color: string) => {
				return changeColor(color, api?.analytics?.actions);
			},
		},

		primaryToolbarComponent: !api?.primaryToolbar ? primaryToolbarComponent : undefined,
	};
};
